import { Outlet, Link, useLocation, matchPath } from 'react-router-dom';
import {
  LayoutDashboard, ClipboardList, PlusCircle, HeartHandshake,
  Banknote, Bell, PawPrint, ScanLine, Menu, X, Home,
  LogOut, User as UserIcon, FileCheck, Settings, CalendarClock,
} from 'lucide-react';
import { useState, useMemo } from 'react';
import { useAuth } from '@rehome/shared';

/**
 * Shelter Layout — sidebar navigation for shelter.rehome.hu
 *
 * Context-aware: shows shelter-scoped menu when a :shelterId is in the URL,
 * otherwise shows global dashboard navigation.
 */
const AdminLayout: React.FC = () => {
  const location = useLocation();
  // NOTE: useParams() cannot be used here — this layout renders ABOVE the
  // /shelter/:shelterId route, and React Router does not expose descendant
  // route params to parent layouts. Match the path manually instead.
  const shelterMatch = matchPath('/shelter/:shelterId/*', location.pathname)
    ?? matchPath('/shelter/:shelterId', location.pathname);
  const shelterId = shelterMatch?.params.shelterId;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, userProfile, signOut } = useAuth();

  const isGlobal = !shelterId;

  const navItems = useMemo(() => {
    if (isGlobal) {
      return [
        { path: '/global', label: 'Központi Irányítópult', icon: LayoutDashboard },
      ];
    }
    return [
      { path: `/shelter/${shelterId}`, label: 'Vezérlőpult', icon: LayoutDashboard, exact: true },
      { path: `/shelter/${shelterId}/intake`, label: 'Állat felvétele', icon: ScanLine },
      { path: `/shelter/${shelterId}/animals`, label: 'Állatok listája', icon: ClipboardList },
      { path: `/shelter/${shelterId}/post-adoption`, label: 'Utókövetés', icon: PawPrint },
      { path: `/shelter/${shelterId}/reminders`, label: 'Emlékeztetők', icon: CalendarClock },
      { path: `/shelter/${shelterId}/leads`, label: 'Örökbefogadási igények', icon: FileCheck },
      { path: `/shelter/${shelterId}/finance`, label: 'Pénzügyek', icon: Banknote },
      { path: `/shelter/${shelterId}/notifications`, label: 'Értesítések', icon: Bell },
      { path: `/shelter/${shelterId}/settings`, label: 'Beállítások', icon: Settings },
    ];
  }, [shelterId, isGlobal]);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-teal-800 text-white transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:static lg:inset-0`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between h-16 px-6 bg-teal-900">
            <Link to="/" className="flex items-center gap-2">
              <PawPrint className="w-6 h-6 text-teal-300" />
              <span className="text-lg font-bold tracking-wider">ReHome Shelter</span>
            </Link>
          </div>

          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {!isGlobal && (
              <Link
                to="/global"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-teal-200 hover:bg-teal-700 transition-colors mb-2"
              >
                <Home className="w-4 h-4" />
                ← Vissza a központhoz
              </Link>
            )}

            {navItems.map(item => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  ((item as any).exact ? location.pathname === item.path : location.pathname.startsWith(item.path))
                    ? 'bg-teal-700 text-white'
                    : 'text-teal-100 hover:bg-teal-700'
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="px-6 py-4 border-t border-teal-700 text-xs text-teal-400">
            ReHome by NJE v1.0
          </div>
        </div>
      </aside>

      {/* Main area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center px-4 lg:px-8 flex-shrink-0">
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 mr-3"
            aria-label="Oldalsáv megnyitása"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-lg font-semibold text-gray-800 truncate flex-1">
            {isGlobal ? 'Központi Irányítópult' : `Menhely kezelése`}
          </h1>

          {/* User menu */}
          <div className="flex items-center gap-3 ml-auto">
            {userProfile && (
              <span className="hidden sm:inline text-sm text-gray-600">
                {userProfile.full_name || user?.email}
              </span>
            )}
            <button
              onClick={() => signOut()}
              title="Kijelentkezés"
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Kilépés</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
